<template>
  <div class="dz-root" ref="root" :style="rootVars" :class="{ 'is-home-turn': turnSide === '1', 'is-away-turn': turnSide === '2' }">
    <div class="dz-stage" :style="{ height: (STAGE_H * scale) + 'px' }">
      <div class="dz-inner" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
        <!-- Arena + board: one flat SVG. The board group pans up ("camera")
             when a dart lands in the lower half, which is otherwise cut off
             by the bottom of the frame. -->
        <svg class="dz-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
          <defs>
            <radialGradient :id="uid + 'bg'" cx="0.5" cy="0.15" r="0.9">
              <stop offset="0" stop-color="#2a5a52" />
              <stop offset="0.55" stop-color="#15302d" />
              <stop offset="1" stop-color="#0a1716" />
            </radialGradient>
            <radialGradient :id="uid + 'haze'" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" :stop-color="turnColor" stop-opacity="0.55" />
              <stop offset="0.6" :stop-color="turnColor" stop-opacity="0.18" />
              <stop offset="1" :stop-color="turnColor" stop-opacity="0" />
            </radialGradient>
            <filter :id="uid + 'glow'" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="5" />
            </filter>
          </defs>

          <rect x="0" y="0" :width="STAGE_W" :height="STAGE_H" :fill="'url(#' + uid + 'bg)'" />
          <!-- Arena light sweeps. -->
          <g fill="none" stroke="rgba(160, 230, 215, 0.07)">
            <ellipse cx="230" cy="250" rx="300" ry="190" stroke-width="30" />
            <ellipse cx="230" cy="250" rx="360" ry="240" stroke-width="14" />
            <ellipse cx="230" cy="250" rx="250" ry="150" stroke-width="6" />
          </g>

          <g class="dz-board" :style="{ transform: 'translate(' + CX + 'px,' + (CY + panY) + 'px)' }">
            <!-- Coloured haze and halo in the colour of the player at the oche
                 (split in the two colours when the turn is unknown). -->
            <circle r="235" :fill="'url(#' + uid + 'haze)'" class="dz-haze" />
            <g :filter="'url(#' + uid + 'glow)'" opacity="0.9">
              <path :d="halfArc(R_HALO, 180, 360)" fill="none" :stroke="haloColors.left" stroke-width="9" />
              <path :d="halfArc(R_HALO, 0, 180)" fill="none" :stroke="haloColors.right" stroke-width="9" />
            </g>
            <path :d="halfArc(R_HALO, 180, 360)" fill="none" :stroke="haloColors.left" stroke-width="2.5" />
            <path :d="halfArc(R_HALO, 0, 180)" fill="none" :stroke="haloColors.right" stroke-width="2.5" />

            <!-- Number ring and numbers. -->
            <circle :r="R.num" fill="#101010" />
            <text v-for="n in numbers" :key="'n' + n.n" :x="n.x" :y="n.y" class="dz-num" text-anchor="middle" dominant-baseline="central">{{ n.n }}</text>

            <!-- Segments. -->
            <path v-for="s in segments" :key="s.key" :d="s.d" :fill="s.fill" />
            <circle :r="R.outer" fill="#2e8f74" />
            <circle :r="R.bull" fill="#c8283c" />

            <!-- Wires. -->
            <g fill="none" stroke="rgba(210, 215, 220, 0.55)" stroke-width="0.7">
              <circle v-for="r in wireRings" :key="'w' + r" :r="r" />
              <path :d="wireSpokes" />
            </g>

            <!-- Hit cells, lit in the thrower's colour. -->
            <path v-for="d in darts" :key="'hl' + d.key" :d="d.cell" class="dz-hit-cell" :fill="d.color" />

            <!-- Darts. -->
            <g v-for="d in darts" :key="'d' + d.key" :transform="'translate(' + d.x.toFixed(1) + ',' + d.y.toFixed(1) + ')'">
              <circle r="4" class="dz-impact" :stroke="d.color" />
              <g class="dz-dart">
                <g class="dz-dart-wobble">
                  <g transform="scale(1.35)">
                    <line x1="2" y1="3" x2="22" y2="-6" stroke="rgba(0,0,0,0.35)" stroke-width="3" stroke-linecap="round" />
                    <line x1="0" y1="0" x2="5" y2="-2.4" stroke="#d9dde2" stroke-width="1.2" />
                    <path d="M4.5,-3.6 L13,-7.6 L14,-5.6 L5.5,-1.4 Z" fill="#30343a" />
                    <line x1="6" y1="-2.6" x2="12.6" y2="-5.8" stroke="#8b939c" stroke-width="0.6" />
                    <line x1="13.5" y1="-6.6" x2="19.5" y2="-9.4" :stroke="d.color" stroke-width="1.4" />
                    <path d="M18,-8.7 L27,-18 L24.5,-9.8 Z" :fill="d.color" />
                    <path d="M18,-8.7 L29,-10.5 L22,-6 Z" fill="#f4f4f4" />
                  </g>
                </g>
              </g>
            </g>
          </g>
        </svg>

        <!-- Player panels. The one at the oche widens while a visit is played
             and holds the three dart chips. -->
        <div class="dz-panel dz-panel--home" :class="panelClass('1')" :style="{ width: panelWidth('1') + 'px' }">
          <div class="dz-avatar"><span>{{ initials(homeName) }}</span></div>
          <div class="dz-panel-text">
            <div class="dz-name">{{ homeName }}</div>
            <div class="dz-rem"><span :key="'rh' + popKey.h" :class="{ 'is-pop': popKey.h > 0 }">{{ remText(shown.h) }}</span></div>
            <div v-if="checkoutHint.h" class="dz-hint">{{ checkoutHint.h }}</div>
          </div>
          <div v-if="visit && visit.side === '1'" class="dz-chips dz-chips--home">
            <span v-for="i in 3" :key="'ch' + i" class="dz-chip" :class="chipClass(i - 1)">
              <span v-if="visit.chips[i - 1]" :key="visit.key + '-' + i" class="dz-chip-text">{{ visit.chips[i - 1] }}</span>
            </span>
          </div>
        </div>
        <div class="dz-panel dz-panel--away" :class="panelClass('2')" :style="{ width: panelWidth('2') + 'px' }">
          <div class="dz-avatar"><span>{{ initials(awayName) }}</span></div>
          <div class="dz-panel-text">
            <div class="dz-name">{{ awayName }}</div>
            <div class="dz-rem"><span :key="'ra' + popKey.a" :class="{ 'is-pop': popKey.a > 0 }">{{ remText(shown.a) }}</span></div>
            <div v-if="checkoutHint.a" class="dz-hint">{{ checkoutHint.a }}</div>
          </div>
          <div v-if="visit && visit.side === '2'" class="dz-chips dz-chips--away">
            <span v-for="i in 3" :key="'ca' + i" class="dz-chip" :class="chipClass(i - 1)">
              <span v-if="visit.chips[i - 1]" :key="visit.key + '-' + i" class="dz-chip-text">{{ visit.chips[i - 1] }}</span>
            </span>
          </div>
        </div>

        <!-- Visit total, rising above the board after the third dart. -->
        <transition name="dz-total">
          <div v-if="totalPop" :key="totalPop.key" class="dz-total" :class="{ 'is-max': totalPop.value === 180, 'is-checkout': totalPop.checkout }" :style="{ '--side-color': totalPop.color }">
            <span v-if="totalPop.checkout" class="dz-total-label">{{ $t('darts.checkout') }}</span>
            <span class="dz-total-value">{{ totalPop.value }}</span>
          </div>
        </transition>

        <!-- Logo at the bottom centre of the frame, between the two bars
             (no floor here: it stays put while the board pans). -->
        <img v-if="showSirplayLogo" :src="PITCH_LOGO" class="dz-logo" alt="" />

        <!-- Bottom bars. -->
        <div class="dz-bar dz-bar--left">
          <template v-if="hasSets">{{ $t('darts.set') }} <b>{{ setNo }}</b> · </template>{{ $t('darts.leg') }} <b>{{ legs.h + legs.a + 1 }}</b>
        </div>
        <div class="dz-bar dz-bar--right">
          <template v-if="hasSets">{{ $t('darts.set') }} <b>{{ sets.h }} - {{ sets.a }}</b> · </template>{{ $t('darts.leg') }} <b>{{ legs.h }} - {{ legs.a }}</b>
        </div>

        <!-- Leg / set won: same two acts as the football goal panel. -->
        <transition name="dz-cel-panel">
          <div v-if="celebration" :key="celebration.key" class="dz-cel" :class="celebration.side === '2' ? 'is-away' : 'is-home'">
            <transition name="dz-cel-act" mode="out-in">
              <div v-if="celAct === 'word'" key="word" class="dz-cel-act dz-cel-act--word">
                <svg class="dz-cel-icon" viewBox="0 0 40 40">
                  <circle cx="20" cy="20" r="17" fill="none" stroke="currentColor" stroke-width="2.4" />
                  <circle cx="20" cy="20" r="10.5" fill="none" stroke="currentColor" stroke-width="2.4" />
                  <circle cx="20" cy="20" r="3.6" fill="currentColor" />
                </svg>
                <div class="dz-cel-word">{{ celebration.title }}</div>
                <div class="dz-cel-name">{{ celebration.name }}</div>
              </div>
              <div v-else key="split" class="dz-cel-act dz-cel-act--split">
                <div class="dz-cel-half dz-cel-half--home">
                  <span class="dz-cel-roll-in dz-cel-half-name"><span>{{ homeName }}</span></span>
                  <span class="dz-cel-digit">
                    <transition name="dz-roll" mode="out-in"><span :key="'h' + celRoll.h" class="dz-cel-digit-value" :class="{ 'is-intro': !celRolled }">{{ celRoll.h }}</span></transition>
                  </span>
                </div>
                <div class="dz-cel-half dz-cel-half--away">
                  <span class="dz-cel-roll-in dz-cel-half-name"><span>{{ awayName }}</span></span>
                  <span class="dz-cel-digit">
                    <transition name="dz-roll" mode="out-in"><span :key="'a' + celRoll.a" class="dz-cel-digit-value" :class="{ 'is-intro': !celRolled }">{{ celRoll.a }}</span></transition>
                  </span>
                </div>
                <div class="dz-cel-caption">{{ celebration.caption }} · {{ celebration.name }}</div>
              </div>
            </transition>
          </div>
        </transition>

        <!-- Match over: badge that stays up. -->
        <transition name="dz-final">
          <div v-if="finalShown" class="dz-final">
            <div class="dz-final-badge">
              <svg class="dz-final-icon" viewBox="0 0 40 40">
                <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
                <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
                <rect x="18" y="22" width="4" height="7" fill="#eab308" />
                <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
              </svg>
              <div class="dz-final-title">{{ $t('statistics.matchfinished') }}</div>
              <div v-if="winnerName" class="dz-final-winner">{{ winnerName }}</div>
              <div class="dz-final-score">
                <template v-if="hasSets">{{ $t('darts.set') }} {{ sets.h }} - {{ sets.a }}</template>
                <template v-else>{{ $t('darts.leg') }} {{ legs.h }} - {{ legs.a }}</template>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { stageScaleMixin, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

const STAGE_W = 460;
const STAGE_H = 230;
// Board centre, below the frame: only the top of the board shows, like the
// broadcast shot.
const CX = 230;
const CY = 262;
// Lowest screen y a dart may land at before the camera pans up to it
// (just above the bottom bars).
const VISIBLE_MAX_Y = 188;

// Board radii (regulation mm scaled so the double ring ends at 144px).
const K = 144 / 170;
const R = { bull: 6.35 * K, outer: 15.9 * K, tIn: 99 * K, tOut: 107 * K, dIn: 162 * K, dOut: 170 * K, num: 172 };
const R_HALO = 178;
const ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const COLORS = { light: '#efd9cf', dark: '#141414', red: '#c8283c', green: '#2e8f74' };

// Timing (ms). Shortened when several visits are waiting.
const T_PAN = 380;
const T_DART = 820;
const T_TOTAL = 500;
const T_HOLD = 1500;
const T_CEL_WORD = 2400;
const T_CEL_ROLL = 3600;
const T_CEL_END = 6600;
// Beyond this many queued steps (e.g. the tab was in the background) the
// backlog is dropped and the scene jumps to the live values.
const MAX_QUEUE = 8;

const polar = (r, a) => {
  const t = a * Math.PI / 180;
  return { x: r * Math.sin(t), y: -r * Math.cos(t) };
};
const f = p => p.x.toFixed(2) + ',' + p.y.toFixed(2);
// Ring sector between radii r1 < r2 and angles a1 < a2 (degrees, clockwise
// from the top).
const sector = (r1, r2, a1, a2) => {
  const p1 = polar(r2, a1);
  const p2 = polar(r2, a2);
  const p3 = polar(r1, a2);
  const p4 = polar(r1, a1);
  if (r1 <= 0) return `M0,0 L${f(p1)} A${r2},${r2} 0 0 1 ${f(p2)} Z`;
  return `M${f(p1)} A${r2},${r2} 0 0 1 ${f(p2)} L${f(p3)} A${r1},${r1} 0 0 0 ${f(p4)} Z`;
};
// '#rrggbb' -> 'rgba(r,g,b,a)'; anything else is returned unchanged.
const withAlpha = (hex, a) => {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(hex || ''));
  return m ? `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})` : hex;
};
const rand = (a, b) => a + Math.random() * (b - a);
const other = s => (s === '1' ? '2' : '1');

// ---- Darts: every possible single dart --------------------------------
// The feed only sends each player's remaining score, not the single darts,
// so a visit's darts are rebuilt from its total: a plausible combination
// weighted toward where players actually aim (treble 20, then 19).
const SEG_W = { 20: 1, 1: 0.5, 5: 0.5, 19: 0.75, 3: 0.4, 7: 0.4, 18: 0.2, 12: 0.2, 17: 0.15, 16: 0.15 };
const AREA = n => ([20, 1, 5].includes(n) ? 'a20' : ([19, 3, 7].includes(n) ? 'a19' : 'x'));
const POOL = [];
for (let n = 1; n <= 20; n++) {
  const w = SEG_W[n] || 0.04;
  POOL.push({ ring: 'S', n, v: n, w, label: 'S' + n });
  POOL.push({ ring: 'T', n, v: 3 * n, w: w * 0.5, label: 'T' + n });
  POOL.push({ ring: 'D', n, v: 2 * n, w: w * 0.07, label: 'D' + n });
}
POOL.push({ ring: 'SB', n: 25, v: 25, w: 0.05, label: '25' });
POOL.push({ ring: 'DB', n: 25, v: 50, w: 0.02, label: 'BULL' });
POOL.push({ ring: 'M', n: 0, v: 0, w: 0.03, label: '0' });
const BY_VALUE = {};
POOL.forEach(d => { (BY_VALUE[d.v] = BY_VALUE[d.v] || []).push(d); });
const FEASIBLE = new Set();
POOL.forEach(a => POOL.forEach(b => POOL.forEach(c => FEASIBLE.add(a.v + b.v + c.v))));

function sampleVisit(total) {
  const combos = [];
  let sum = 0;
  POOL.forEach(a => {
    POOL.forEach(b => {
      (BY_VALUE[total - a.v - b.v] || []).forEach(c => {
        const areas = new Set([a, b, c].filter(d => d.n >= 1 && d.n <= 20).map(d => AREA(d.n)));
        const w = a.w * b.w * c.w * (areas.size === 1 && !areas.has('x') ? 3 : 1);
        sum += w;
        combos.push({ w, darts: [a, b, c] });
      });
    });
  });
  if (!combos.length) return null;
  let r = Math.random() * sum;
  for (const c of combos) {
    r -= c.w;
    if (r <= 0) return c.darts;
  }
  return combos[combos.length - 1].darts;
}

// ---- Checkouts -----------------------------------------------------------
const find = (ring, n) => POOL.find(d => d.ring === ring && d.n === n);
const DOUBLES = [20, 16, 8, 18, 12, 10, 4, 25, 19, 17, 15, 14, 13, 11, 9, 7, 6, 5, 3, 2, 1]
  .map(n => (n === 25 ? find('DB', 25) : find('D', n)));
const SETUP = [
  ...[20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10].map(n => find('T', n)),
  find('DB', 25), find('SB', 25),
  ...[20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => find('S', n)),
  ...[9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => find('T', n)),
  ...[20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(n => find('D', n))
];
const CHECKOUT_CACHE = {};
// Fewest darts first, then the route that stays on the big trebles and
// finishes on a favourite double (T20 T16 D8 for 124, T20 T20 BULL for 170).
function checkoutRoute(r) {
  if (r in CHECKOUT_CACHE) return CHECKOUT_CACHE[r];
  let best = null;
  if (r >= 2 && r <= 170) {
    const one = DOUBLES.find(d => d.v === r);
    if (one) best = [one];
    for (let len = 2; len <= 3 && !best; len++) {
      let bestCost = Infinity;
      DOUBLES.forEach((d, di) => {
        SETUP.forEach((a, ai) => {
          if (len === 2) {
            if (a.v + d.v !== r) return;
            const cost = ai + 2 * di;
            if (cost < bestCost) { bestCost = cost; best = [a, d]; }
            return;
          }
          SETUP.forEach((b, bi) => {
            if (a.v + b.v + d.v !== r) return;
            const cost = ai + bi + 2 * di;
            if (cost < bestCost) { bestCost = cost; best = [a, b, d]; }
          });
        });
      });
    }
  }
  CHECKOUT_CACHE[r] = best;
  return best;
}

// A score drop too big for one visit (the feed skipped some updates), split
// into visits a player can actually throw.
function splitDrop(total) {
  if (total <= 0) return [];
  // Even visits of at most ~160, so none lands on a total no visit can
  // make (163, 166, 169, 172, ... 179).
  const n = Math.max(1, total <= 180 && FEASIBLE.has(total) ? 1 : Math.ceil(total / 160));
  const base = Math.floor(total / n);
  const parts = Array(n).fill(base);
  parts[0] += total - base * n;
  for (let i = 0; i < parts.length; i++) {
    let guard = 0;
    while (!FEASIBLE.has(parts[i]) && guard++ < 10) {
      parts[i] -= 1;
      if (i + 1 < parts.length) parts[i + 1] += 1;
      else parts.push(1);
    }
  }
  return parts.filter(p => p > 0);
}

// Visits that take a player from `from` down to exactly 0.
function checkoutVisits(from) {
  if (checkoutRoute(from)) return [{ total: from, route: checkoutRoute(from) }];
  const leaves = [40, 32, 36, 16, 50, 24, 20, 8];
  for (let l = 2; l <= 170; l++) if (!leaves.includes(l)) leaves.push(l);
  for (const leave of leaves) {
    const setup = from - leave;
    if (setup > 0 && setup <= 180 && FEASIBLE.has(setup) && checkoutRoute(leave)) {
      return [{ total: setup }, { total: leave, route: checkoutRoute(leave) }];
    }
  }
  const leave = 40;
  return [...splitDrop(from - leave).map(total => ({ total })), { total: leave, route: checkoutRoute(leave) }];
}

// Where a dart sits on the board (local coords, board centre at 0,0) and
// the cell it lights up.
function placeDart(d) {
  if (d.ring === 'DB') {
    const p = polar(rand(0, R.bull - 1.5), rand(0, 360));
    return { x: p.x, y: p.y, cell: sector(0, R.bull, 0, 359.9) };
  }
  if (d.ring === 'SB') {
    const p = polar(rand(R.bull + 1.5, R.outer - 1.5), rand(0, 360));
    return { x: p.x, y: p.y, cell: `${sector(R.bull, R.outer, 0, 180)} ${sector(R.bull, R.outer, 180, 359.9)}` };
  }
  const idx = d.ring === 'M' ? Math.floor(Math.random() * 20) : ORDER.indexOf(d.n);
  const a1 = idx * 18 - 9;
  const a = idx * 18 + rand(-6, 6);
  let r1;
  let r2;
  let r;
  if (d.ring === 'T') { r1 = R.tIn; r2 = R.tOut; r = rand(r1 + 1.5, r2 - 1.5); }
  else if (d.ring === 'D') { r1 = R.dIn; r2 = R.dOut; r = rand(r1 + 1.5, r2 - 1.5); }
  else if (d.ring === 'M') { r1 = R.dOut; r2 = R.num; r = rand(r1 + 5, r2 - 4); }
  else if (Math.random() < 0.3) { r1 = R.outer; r2 = R.tIn; r = rand(r1 + 8, r2 - 4); }
  else { r1 = R.tOut; r2 = R.dIn; r = rand(r1 + 4, r2 - 4); }
  const p = polar(r, a);
  return { x: p.x, y: p.y, cell: d.ring === 'M' ? '' : sector(r1, r2, a1, a1 + 18) };
}

export default {
  name: 'DartsArena',
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    // Points left in the current leg (null when the feed has none).
    remainingH: { type: Number, default: null },
    remainingA: { type: Number, default: null },
    // Legs won in the current set, sets won in the match.
    legsH: { type: Number, default: 0 },
    legsA: { type: Number, default: 0 },
    setsH: { type: Number, default: 0 },
    setsA: { type: Number, default: 0 },
    setNo: { type: Number, default: 1 },
    hasSets: { type: Boolean, default: false },
    // '1' home / '2' away / null: who is at the oche.
    turn: { type: String, default: null },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    finished: { type: Boolean, default: false }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      CX,
      CY,
      R,
      R_HALO,
      // Values on screen: they lag the props while visits are animated.
      shown: { h: this.remainingH, a: this.remainingA },
      legs: { h: this.legsH, a: this.legsA },
      sets: { h: this.setsH, a: this.setsA },
      // Last props already turned into queued steps.
      last: this.snapshot(),
      queue: [],
      busy: false,
      timers: [],
      panY: 0,
      darts: [],
      visit: null,
      totalPop: null,
      popKey: { h: 0, a: 0 },
      celebration: null,
      celAct: 'word',
      celRolled: false,
      celRoll: { h: 0, a: 0 },
      seq: 0
    };
  },
  watch: {
    feedKey() {
      this.onFeed();
    },
    finished(val) {
      if (val) this.onFeed();
    }
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    snapshot() {
      return {
        rem: { h: this.remainingH, a: this.remainingA },
        legs: { h: this.legsH, a: this.legsA },
        sets: { h: this.setsH, a: this.setsA }
      };
    },
    halfArc(r, a1, a2) {
      return `M${f(polar(r, a1))} A${r},${r} 0 0 1 ${f(polar(r, a2))}`;
    },
    initials(name) {
      return String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
    },
    remText(v) {
      return v === null || v === undefined ? '-' : v;
    },
    sideColor(side) {
      return side === '1' ? (this.homeColor || '#3b82f6') : (this.awayColor || '#e11d48');
    },
    panelClass(side) {
      const active = this.visit ? this.visit.side === side : this.turnSide === side;
      return { 'is-active': active, 'is-dim': !!this.visit && this.visit.side !== side };
    },
    panelWidth(side) {
      if (!this.visit) return STAGE_W / 2;
      return this.visit.side === side ? 345 : STAGE_W - 345;
    },
    chipClass(i) {
      const v = this.visit;
      return { 'is-filled': !!v.chips[i], 'is-last': v.chips.length - 1 === i };
    },
    later(ms, fn) {
      const id = setTimeout(() => {
        this.timers = this.timers.filter(t => t !== id);
        fn();
      }, ms);
      this.timers.push(id);
    },
    clearTimers() {
      this.timers.forEach(clearTimeout);
      this.timers = [];
    },
    // Turns the difference between the last props and the new ones into
    // queued steps: visits (score going down), leg/set celebrations.
    onFeed() {
      const prev = this.last;
      const next = this.snapshot();
      this.last = next;
      // First livescore after mount: show it as it is, nothing to replay.
      if (prev.rem.h === null && prev.rem.a === null) {
        this.queue.push({ type: 'sync', to: next });
        if (this.finished) this.queue.push({ type: 'final' });
        this.pump();
        return;
      }
      const setWon = next.sets.h > prev.sets.h ? '1' : (next.sets.a > prev.sets.a ? '2' : null);
      const legWon = setWon || (next.legs.h > prev.legs.h ? '1' : (next.legs.a > prev.legs.a ? '2' : null));
      const lastThrower = this.turn === '1' || this.turn === '2' ? other(this.turn) : null;

      if (legWon) {
        const key = legWon === '1' ? 'h' : 'a';
        const from = prev.rem[key];
        if (from !== null && from > 0) {
          checkoutVisits(from).forEach((v, i, all) => {
            this.queue.push({ type: 'visit', side: legWon, total: v.total, route: v.route, checkout: i === all.length - 1 });
          });
        }
        this.queue.push({
          type: 'celebrate',
          side: legWon,
          kind: setWon ? 'set' : 'leg',
          from: setWon ? { ...prev.sets } : { ...prev.legs },
          to: setWon ? { ...next.sets } : { ...next.legs },
          after: next
        });
        // Visits already thrown in the new leg.
        this.queueVisits(501, 501, next.rem, lastThrower);
      } else {
        const lower = (a, b) => a !== null && b !== null && b < a;
        const raised = k => prev.rem[k] !== null && next.rem[k] !== null && next.rem[k] > prev.rem[k];
        if (lower(prev.rem.h, next.rem.h) || lower(prev.rem.a, next.rem.a)) {
          this.queueVisits(prev.rem.h, prev.rem.a, next.rem, lastThrower);
        }
        if (raised('h') || raised('a') || prev.rem.h === null || prev.rem.a === null ||
            next.legs.h !== prev.legs.h || next.legs.a !== prev.legs.a || next.sets.h !== prev.sets.h || next.sets.a !== prev.sets.a) {
          this.queue.push({ type: 'sync', to: next });
        }
      }
      if (this.finished) this.queue.push({ type: 'final' });
      if (this.queue.length > MAX_QUEUE) {
        this.queue = [];
        this.clearTimers();
        this.resetScene();
        this.syncTo(next);
      }
      this.pump();
    },
    // Visits for both players, interleaved so that whoever threw last (the
    // one no longer at the oche) comes last.
    queueVisits(fromH, fromA, to, lastThrower) {
      const chunks = (from, dest) => (from !== null && dest !== null && dest < from ? splitDrop(from - dest) : []);
      const h = chunks(fromH, to.h).map(total => ({ type: 'visit', side: '1', total }));
      const a = chunks(fromA, to.a).map(total => ({ type: 'visit', side: '2', total }));
      const order = [];
      let s = lastThrower || (h.length >= a.length ? '1' : '2');
      while (h.length || a.length) {
        const list = s === '1' ? h : a;
        const step = list.length ? list.pop() : (s === '1' ? a : h).pop();
        order.unshift(step);
        s = other(s);
      }
      this.queue.push(...order);
    },
    syncTo(snap) {
      this.shown = { ...snap.rem };
      this.legs = { ...snap.legs };
      this.sets = { ...snap.sets };
    },
    resetScene() {
      this.visit = null;
      this.darts = [];
      this.totalPop = null;
      this.celebration = null;
      this.panY = 0;
      this.busy = false;
    },
    speed() {
      return this.queue.length > 2 ? 0.55 : 1;
    },
    pump() {
      if (this.busy || !this.queue.length) return;
      const step = this.queue.shift();
      this.busy = true;
      const done = () => { this.busy = false; this.pump(); };
      if (step.type === 'visit') this.playVisit(step, done);
      else if (step.type === 'celebrate') this.playCelebration(step, done);
      else if (step.type === 'sync') { this.syncTo(step.to); done(); }
      else done();
    },
    playVisit(step, done) {
      const key = step.side === '1' ? 'h' : 'a';
      const k = this.speed();
      const darts = step.route || sampleVisit(step.total) || [];
      const color = this.sideColor(step.side);
      this.seq++;
      this.darts = [];
      this.visit = { key: this.seq, side: step.side, chips: [] };
      let t = 350 * k;
      let curPan = this.panY;
      darts.forEach(d => {
        const place = placeDart(d);
        const screenY = CY + place.y;
        const pan = screenY > VISIBLE_MAX_Y ? VISIBLE_MAX_Y - screenY : 0;
        if (pan !== curPan) {
          this.later(t, () => { this.panY = pan; });
          t += T_PAN * k;
          curPan = pan;
        }
        this.later(t, () => {
          this.darts.push({ key: this.seq + '-' + this.darts.length, x: place.x, y: place.y, cell: place.cell, color });
          this.visit.chips.push(d.label);
          if (this.shown[key] !== null) {
            this.shown[key] = Math.max(0, this.shown[key] - d.v);
            this.popKey[key]++;
          }
        });
        t += T_DART * k;
      });
      this.later(t + T_TOTAL * k - T_DART * k * 0.4, () => {
        this.totalPop = { key: this.seq, value: step.total, color, checkout: !!step.checkout };
      });
      t += T_TOTAL * k + T_HOLD * k;
      this.later(t, () => {
        this.totalPop = null;
        this.visit = null;
        this.darts = [];
        this.panY = 0;
      });
      this.later(t + 350 * k, done);
    },
    playCelebration(step, done) {
      this.seq++;
      const name = step.side === '1' ? this.homeName : this.awayName;
      this.celebration = {
        key: this.seq,
        side: step.side,
        name,
        title: this.$t(step.kind === 'set' ? 'darts.set' : 'darts.leg') + '!',
        caption: this.$t(step.kind === 'set' ? 'darts.setwon' : 'darts.legwon')
      };
      this.celAct = 'word';
      this.celRolled = false;
      this.celRoll = { ...step.from };
      this.later(T_CEL_WORD, () => { this.celAct = 'split'; });
      this.later(T_CEL_ROLL, () => {
        this.celRolled = true;
        this.celRoll = { ...step.to };
      });
      this.later(T_CEL_END, () => {
        this.celebration = null;
        this.syncTo(step.after);
        // The new leg starts from 501 until its own visits are replayed.
        this.shown = { h: step.after.rem.h === null ? null : 501, a: step.after.rem.a === null ? null : 501 };
      });
      this.later(T_CEL_END + 400, done);
    }
  },
  computed: {
    uid() { return 'dz' + this._uid + '-'; },
    feedKey() {
      return [this.remainingH, this.remainingA, this.legsH, this.legsA, this.setsH, this.setsA].join('|');
    },
    turnSide() {
      if (this.visit) return this.visit.side;
      return this.turn === '1' || this.turn === '2' ? this.turn : null;
    },
    turnColor() {
      return this.turnSide ? this.sideColor(this.turnSide) : 'rgba(0,0,0,0)';
    },
    haloColors() {
      if (this.turnSide) return { left: this.turnColor, right: this.turnColor };
      return { left: this.sideColor('1'), right: this.sideColor('2') };
    },
    rootVars() {
      return {
        '--dz-home': this.sideColor('1'),
        '--dz-away': this.sideColor('2'),
        '--dz-home-soft': withAlpha(this.sideColor('1'), 0.6),
        '--dz-away-soft': withAlpha(this.sideColor('2'), 0.6),
        '--dz-home-glow': withAlpha(this.sideColor('1'), 0.35),
        '--dz-away-glow': withAlpha(this.sideColor('2'), 0.35)
      };
    },
    finalShown() {
      return this.finished && !this.busy && !this.queue.length;
    },
    winnerName() {
      const h = this.hasSets ? this.sets.h : this.legs.h;
      const a = this.hasSets ? this.sets.a : this.legs.a;
      if (h === a) return '';
      return h > a ? this.homeName : this.awayName;
    },
    checkoutHint() {
      const hint = v => {
        if (v === null || v === undefined || this.finished) return '';
        const route = checkoutRoute(v);
        return route ? route.map(d => d.label).join(', ') : '';
      };
      return { h: hint(this.shown.h), a: hint(this.shown.a) };
    },
    numbers() {
      return ORDER.map((n, i) => ({ n, ...polar(158, i * 18) }));
    },
    segments() {
      const out = [];
      ORDER.forEach((n, i) => {
        const a1 = i * 18 - 9;
        const a2 = a1 + 18;
        const light = i % 2 === 0;
        const base = light ? COLORS.light : COLORS.dark;
        const ring = light ? COLORS.red : COLORS.green;
        out.push({ key: n + 'si', d: sector(R.outer, R.tIn, a1, a2), fill: base });
        out.push({ key: n + 't', d: sector(R.tIn, R.tOut, a1, a2), fill: ring });
        out.push({ key: n + 'so', d: sector(R.tOut, R.dIn, a1, a2), fill: base });
        out.push({ key: n + 'd', d: sector(R.dIn, R.dOut, a1, a2), fill: ring });
      });
      return out;
    },
    wireRings() {
      return [R.bull, R.outer, R.tIn, R.tOut, R.dIn, R.dOut];
    },
    wireSpokes() {
      return ORDER.map((n, i) => `M${f(polar(R.outer, i * 18 - 9))} L${f(polar(R.dOut, i * 18 - 9))}`).join(' ');
    }
  }
};
</script>

<style scoped>
.dz-root { position: relative; width: 100%; overflow: hidden; background: #0a1716; font-family: Roboto, Arial, sans-serif; }
.dz-stage { position: relative; width: 100%; overflow: hidden; }
.dz-inner { position: absolute; top: 0; left: 0; width: 460px; height: 230px; transform-origin: 0 0; }
.dz-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.dz-board { transition: transform 0.38s cubic-bezier(.3, .7, .3, 1); }
.dz-haze { transition: opacity 0.4s ease; }
.dz-num { fill: #fff; font-size: 14px; font-weight: 500; font-family: Roboto, Arial, sans-serif; }

/* Hit cell: flashes in the thrower's colour, then stays lit. */
.dz-hit-cell { animation: dz-cell 0.9s ease-out 0.3s both; }
@keyframes dz-cell { 0% { opacity: 0; } 25% { opacity: 1; } 55% { opacity: 0.55; } 100% { opacity: 1; } }

/* Dart flying in from the thrower, big and blurred, then sticking. */
.dz-dart { animation: dz-fly 0.34s cubic-bezier(.25, .6, .35, 1) both; }
@keyframes dz-fly {
  0% { transform: translate(70px, 150px) scale(3.4); opacity: 0; }
  30% { opacity: 1; }
  100% { transform: translate(0, 0) scale(1); opacity: 1; }
}
.dz-dart-wobble { animation: dz-wobble 0.5s ease-out 0.34s both; }
@keyframes dz-wobble { 0% { transform: rotate(0); } 25% { transform: rotate(-7deg); } 55% { transform: rotate(4deg); } 80% { transform: rotate(-2deg); } 100% { transform: rotate(0); } }
.dz-impact { fill: none; stroke-width: 1.6; opacity: 0; animation: dz-impact 0.6s ease-out 0.3s both; transform-box: fill-box; transform-origin: 50% 50%; }
@keyframes dz-impact { 0% { transform: scale(0.4); opacity: 1; } 100% { transform: scale(4); opacity: 0; } }

/* ---------- player panels ---------- */
.dz-panel {
  position: absolute; top: 0; height: 70px; box-sizing: border-box; overflow: hidden; z-index: 2;
  background: linear-gradient(180deg, rgba(6, 14, 14, 0.62) 0%, rgba(6, 14, 14, 0.35) 100%);
  border: 1px solid transparent;
  transition: width 0.45s cubic-bezier(.3, .8, .3, 1), border-color 0.3s ease, box-shadow 0.3s ease;
}
.dz-panel--home { left: 0; border-bottom-color: var(--dz-home-soft); }
.dz-panel--away { right: 0; border-bottom-color: var(--dz-away-soft); border-left-color: var(--dz-away-soft); }
.dz-panel--home.is-active { border-color: var(--dz-home); box-shadow: inset 0 0 18px var(--dz-home-glow); }
.dz-panel--away.is-active { border-color: var(--dz-away); box-shadow: inset 0 0 18px var(--dz-away-glow); }
.dz-panel.is-dim .dz-name, .dz-panel.is-dim .dz-rem { color: #8a9695; }

.dz-avatar {
  position: absolute; top: 5px; width: 54px; height: 54px; border-radius: 50%; display: flex; align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.5); border: 2px solid rgba(255, 255, 255, 0.85); box-sizing: border-box;
  font-weight: 900; font-size: var(--font-size-4xl); color: #fff;
}
.dz-panel--home .dz-avatar { left: -18px; justify-content: flex-end; padding-right: 9px; background: radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 60%), var(--dz-home); }
.dz-panel--away .dz-avatar { right: -18px; justify-content: flex-start; padding-left: 9px; background: radial-gradient(circle at 35% 30%, rgba(255, 255, 255, 0.45) 0%, rgba(255, 255, 255, 0) 60%), var(--dz-away); }

.dz-panel-text { position: absolute; top: 6px; display: flex; flex-direction: column; min-width: 0; max-width: 150px; }
.dz-panel--home .dz-panel-text { left: 44px; align-items: flex-start; }
.dz-panel--away .dz-panel-text { right: 44px; align-items: flex-end; text-align: right; }
.dz-name { color: #fff; font-weight: 700; font-size: var(--font-size-3xl); line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; transition: color 0.3s; }
.dz-rem { color: #fff; font-weight: 800; font-size: var(--font-size-10xl); line-height: 1.1; font-variant-numeric: tabular-nums; transition: color 0.3s; }
.dz-rem .is-pop { display: inline-block; animation: dz-pop 0.45s cubic-bezier(.2, 1.6, .4, 1) both; }
@keyframes dz-pop { 0% { transform: scale(0.7); opacity: 0.4; } 50% { transform: scale(1.18); } 100% { transform: scale(1); opacity: 1; } }
.dz-hint { color: #8fa3a1; font-size: var(--font-size-xl); font-weight: 500; white-space: nowrap; }

/* Dart chips: the score of each dart of the visit. */
.dz-chips { position: absolute; top: 23px; display: flex; gap: 4px; }
.dz-chips--away { left: 72px; }
.dz-chips--home { right: 72px; }
.dz-chip {
  width: 46px; height: 24px; box-sizing: border-box; display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.5); border: 1px solid transparent; border-radius: 2px;
  animation: dz-chip-in 0.3s ease-out both;
}
.dz-chip:nth-child(2) { animation-delay: 0.05s; }
.dz-chip:nth-child(3) { animation-delay: 0.1s; }
.dz-chips--home .dz-chip.is-last { border-color: var(--dz-home); }
.dz-chips--away .dz-chip.is-last { border-color: var(--dz-away); }
.dz-chip-text { color: #fff; font-weight: 800; font-style: italic; font-size: var(--font-size-2xl); animation: dz-chip-text 0.4s cubic-bezier(.2, 1.5, .4, 1) both; }
@keyframes dz-chip-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
@keyframes dz-chip-text { from { opacity: 0; transform: scale(1.8); } to { opacity: 1; transform: scale(1); } }

/* ---------- visit total ---------- */
.dz-total {
  position: absolute; left: 50%; top: 84px; transform: translateX(-50%); z-index: 3; pointer-events: none;
  display: flex; flex-direction: column; align-items: center;
}
.dz-total-value {
  padding: 1px 12px; border-radius: 4px; background: rgba(6, 12, 12, 0.78); border: 1px solid var(--side-color);
  color: #fff; font-weight: 900; font-style: italic; font-size: var(--font-size-10xl); line-height: 1.15;
  box-shadow: 0 0 14px var(--side-color);
  animation: dz-total-in 0.5s cubic-bezier(.2, 1.5, .4, 1) both;
}
.dz-total-label { margin-bottom: 2px; padding: 1px 8px; border-radius: 3px; background: var(--side-color); color: #fff; font-weight: 800; font-size: var(--font-size-xl); text-transform: uppercase; animation: dz-total-in 0.4s ease-out 0.1s both; }
.dz-total.is-max .dz-total-value { font-size: var(--font-size-17xl); color: #facc15; border-color: #facc15; box-shadow: 0 0 26px rgba(250, 204, 21, 0.7); animation: dz-max 0.9s cubic-bezier(.2, 1.4, .4, 1) both; }
@keyframes dz-total-in { from { opacity: 0; transform: translateY(12px) scale(0.6); } to { opacity: 1; transform: translateY(0) scale(1); } }
@keyframes dz-max { 0% { opacity: 0; transform: scale(2.6) rotate(-8deg); } 45% { opacity: 1; transform: scale(0.9) rotate(2deg); } 65% { transform: scale(1.1) rotate(-1deg); } 100% { transform: scale(1) rotate(0); } }
.dz-total-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.dz-total-leave-to { opacity: 0; transform: translate(-50%, -12px); }

/* ---------- bottom bars ---------- */
.dz-bar {
  position: absolute; bottom: 0; height: 22px; display: flex; align-items: center; gap: 3px; padding: 0 9px; z-index: 2;
  background: rgba(8, 12, 12, 0.78); color: #e5eceb; font-size: var(--font-size-xl); white-space: nowrap;
}
.dz-bar b { color: #fff; font-weight: 800; }
.dz-bar--left { left: 0; }
.dz-bar--right { right: 0; }

/* ---------- leg / set celebration ---------- */
.dz-cel {
  --cel-color: var(--dz-home);
  position: absolute; inset: 0; z-index: 6; overflow: hidden; box-sizing: border-box;
  background: linear-gradient(100deg, rgba(5, 14, 13, 0.96) 0%, rgba(14, 36, 33, 0.95) 50%, rgba(5, 14, 13, 0.96) 100%);
  border-bottom: 2px solid var(--cel-color);
}
.dz-cel.is-away { --cel-color: var(--dz-away); }
.dz-cel-act { position: absolute; inset: 0; }
.dz-cel-act--word { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.dz-cel-icon { width: 38px; height: 38px; color: var(--cel-color); margin-bottom: 4px; animation: dz-cel-icon 0.8s cubic-bezier(.2, 1.4, .4, 1) both; }
@keyframes dz-cel-icon { from { transform: scale(0.2) rotate(-200deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.dz-cel-word {
  font: italic 900 var(--font-size-20xl)/0.95 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  text-shadow: 0 0 18px var(--cel-color), 0 3px 0 rgba(0, 0, 0, 0.45);
  animation: dz-cel-word 0.55s cubic-bezier(.2, 1.4, .4, 1) 0.3s both;
}
@keyframes dz-cel-word { from { opacity: 0; transform: scale(2.2); } to { opacity: 1; transform: scale(1); } }
.dz-cel-name { margin-top: 6px; font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: var(--cel-color); filter: brightness(1.3); animation: dz-cel-fade-up 0.4s ease-out 0.6s both; }
@keyframes dz-cel-fade-up { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.dz-cel-act-enter-active { transition: opacity 0.35s ease; }
.dz-cel-act-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.dz-cel-act-enter { opacity: 0; }
.dz-cel-act-leave-to { opacity: 0; transform: translateY(-18px); }

.dz-cel-act--split { display: flex; }
.dz-cel-half { position: relative; flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 0; animation: dz-cel-half-in 0.45s cubic-bezier(.2, .9, .2, 1) both; }
.dz-cel-half--home { background: linear-gradient(90deg, rgba(255, 255, 255, 0.14) 0%, rgba(0, 0, 0, 0.32) 100%), var(--dz-home); }
.dz-cel-half--away { background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 0%, rgba(0, 0, 0, 0.4) 100%), var(--dz-away); animation-delay: 0.08s; }
@keyframes dz-cel-half-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
.dz-cel-roll-in { display: block; overflow: hidden; }
.dz-cel-roll-in > span { display: block; animation: dz-cel-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) both; }
.dz-cel-half-name { max-width: 90%; margin-bottom: 4px; }
.dz-cel-half-name > span {
  font: 800 var(--font-size-3xl)/1.3 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  animation-delay: 0.45s;
}
.dz-cel-half--away .dz-cel-half-name > span { animation-delay: 0.55s; }
@keyframes dz-cel-roll-up { from { transform: translateY(110%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.dz-cel-digit { position: relative; width: 110px; height: 96px; overflow: hidden; }
.dz-cel-digit-value {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: italic 900 calc(var(--font-size-20xl) * 1.4)/1 'Roboto', sans-serif; color: #fff; font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}
.dz-cel-digit-value.is-intro { animation: dz-cel-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) 0.65s both; }
.dz-roll-leave-active { transition: transform 0.22s cubic-bezier(.5, 0, .9, .4); }
.dz-roll-leave-to { transform: translateY(-110%); }
.dz-roll-enter-active { transition: transform 0.38s cubic-bezier(.15, .85, .35, 1); }
.dz-roll-enter { transform: translateY(110%); }
.dz-cel-caption {
  position: absolute; left: 50%; bottom: 14px; transform: translateX(-50%);
  max-width: 90%; box-sizing: border-box; padding: 4px 12px; border-radius: 4px;
  background: rgba(0, 0, 0, 0.45); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font: 800 var(--font-size-2xl)/1.2 'Roboto', sans-serif; color: #fff; text-transform: uppercase;
  animation: dz-cel-caption 0.4s ease-out 0.9s both;
}
@keyframes dz-cel-caption { from { opacity: 0; transform: translate(-50%, 10px); } to { opacity: 1; transform: translate(-50%, 0); } }
.dz-cel-panel-enter-active { transition: transform 0.5s cubic-bezier(.2, .9, .2, 1); }
.dz-cel-panel-leave-active { transition: transform 0.45s cubic-bezier(.6, 0, .8, .3); }
.dz-cel-panel-enter { transform: translateX(-105%); }
.dz-cel-panel-leave-to { transform: translateX(105%); }

/* ---------- match over ---------- */
.dz-final { position: absolute; inset: 0; z-index: 7; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 12, 0.62); }
.dz-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 12px 30px 14px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: dz-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.dz-final-icon { width: 38px; height: 38px; animation: dz-trophy 2s ease-in-out 0.7s infinite; }
.dz-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.dz-final-winner { color: #fff; font-size: var(--font-size-5xl); font-weight: 700; }
.dz-final-score { color: #cbd5e1; font-size: var(--font-size-3xl); font-weight: 700; }
@keyframes dz-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes dz-trophy { 0%, 100% { transform: translateY(0) rotate(0); } 25% { transform: translateY(-3px) rotate(-6deg); } 75% { transform: translateY(-3px) rotate(6deg); } }
.dz-final-enter-active { transition: opacity 0.3s; }
.dz-final-enter { opacity: 0; }
.dz-logo {
  position: absolute; left: 50%; bottom: 0; height: 22px; box-sizing: border-box; padding: 4px 9px; transform: translateX(-50%); z-index: 2; pointer-events: none;
  background: rgba(8, 12, 12, 0.78);
}
</style>
